import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { ChevronDown } from 'lucide-react'
import { useDateFormatter, useLocalizationConfig } from '@/hooks/useDateFormatter'
import { localToday } from '@/lib/date'
import { useTeamToday, type TeamTodayRow } from '@/hooks/useShiftClock'

/**
 * EP-154: who is clocked in right now, for the admin's dashboard. The
 * owner's review surface in place of a push (owner, 2026-10-06).
 *
 * Collapsed by default to one line: one dot per member, the colour saying
 * where they are (green in, red still open from an earlier day, grey not in),
 * the name and state on hover; the dots open the list (owner, 2026-10-07:
 * the card was too big). Same shell as the "Today" strip below it.
 */

function formatHours(hours: number, t: (k: string, o?: Record<string, unknown>) => string): string {
  const total = Math.round(hours * 60)
  return t('dashboard.shift.duration', { hours: Math.floor(total / 60), minutes: total % 60 })
}

function elapsedHours(row: TeamTodayRow, now: number): number {
  const at = row.open?.clock_in_at
  if (!at) return 0
  return Math.max(0, (now - new Date(at).getTime()) / 3_600_000)
}

export function TeamTodayCard() {
  const { t } = useTranslation('common')
  const formatDate = useDateFormatter()
  const { timezone } = useLocalizationConfig()
  const today = localToday(timezone)
  const { data } = useTeamToday()
  const [expanded, setExpanded] = useState(false)
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000)
    return () => window.clearInterval(id)
  }, [])

  if (!data) return null

  const rows = data.users.map((row) => {
    const open = row.open
    const stale = !!open && open.work_date !== today
    const dot = open ? (stale ? 'bg-error' : 'bg-success') : 'bg-base-content/25'
    const tone = open ? (stale ? 'text-error' : 'text-base-content/70') : row.hours_today > 0 ? 'text-base-content/70' : 'text-base-content/50'
    const state = open
      ? `${stale
          ? t('dashboard.team_today.open_since', { date: formatDate(open.work_date), time: open.clock_in })
          : t('dashboard.team_today.since', { time: open.clock_in })} ${formatHours(elapsedHours(row, now), t)}`
      : row.hours_today > 0
        ? formatHours(row.hours_today, t)
        : t('dashboard.team_today.not_started')
    return { row, dot, tone, state }
  })
  const inCount = data.users.filter((r) => r.open).length

  return (
    <div className="rounded-lg border border-base-200 bg-base-100 px-4 py-3 space-y-2" data-testid="team-today-card">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h3 className="text-sm font-semibold text-base-content/80">{t('dashboard.team_today.title')}</h3>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label={expanded ? t('dashboard.team_today.hide') : t('dashboard.team_today.show')}
          className="flex min-w-0 items-center gap-2 rounded-md text-left"
          data-testid="team-today-toggle"
        >
          <span className="flex flex-wrap items-center gap-1.5" data-testid="team-today-dots">
            {rows.map(({ row, dot, state }) => (
              <span
                key={row.user.id}
                role="img"
                aria-label={`${row.user.name}: ${state}`}
                title={`${row.user.name}: ${state}`}
                className={`inline-block w-3 h-3 rounded-full ${dot}`}
              />
            ))}
          </span>
          <span className="text-xs text-base-content/60 tabular-nums whitespace-nowrap" data-testid="team-today-count">
            {t('dashboard.team_today.in_count', { present: inCount, total: data.users.length })}
          </span>
          <ChevronDown className={`w-4 h-4 shrink-0 text-base-content/60 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
        <Link to="/timesheet" className="ml-auto text-xs text-primary hover:underline whitespace-nowrap">
          {t('dashboard.team_today.view_timesheet')}
        </Link>
      </div>

      {expanded && (
        <ul className="divide-y divide-base-200 border-t border-base-200" data-testid="team-today-rows">
          {rows.map(({ row, dot, tone, state }) => (
            <li key={row.user.id} className="flex items-center gap-3 py-1.5 text-sm">
              <span className={`inline-block w-2 h-2 rounded-full ${dot}`} aria-hidden />
              <span className="flex-1 min-w-0 truncate">{row.user.name}</span>
              <span className={`text-xs ${tone}`}>{state}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
